import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  serverTimestamp,
  query,
  where,
  getDoc,
  updateDoc,
  doc
} from 'firebase/firestore';
import { onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { deleteDoc } from 'firebase/firestore';

function PhotoUpload() {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const unsubscribeCommentsRef = useRef(null);

  // Utility to enrich a photo with likeProfiles (by phoneNumber)
  const enrichPhotoWithLikes = async (photo) => {
    const invitesSnap = await getDocs(collection(db, 'invites'));
    const invites = invitesSnap.docs.map(d => d.data());
    const likeProfiles = (photo.likes || []).map(phone => {
      const match = invites.find(inv => inv.phoneNumber === phone);
      return match
        ? {
            phoneNumber: phone,
            name: match.name,
            photoUrl: match.photoUrl && match.photoUrl.trim() !== '' ? match.photoUrl : 'https://placehold.co/100'
          }
        : null;
    }).filter(Boolean);
    return { ...photo, likeProfiles };
  };

  const fetchPhotos = async () => {
    const snapshot = await getDocs(collection(db, 'guestGallery'));
    const fetched = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Get all invites for enriching likeProfiles
    const invitesSnap = await getDocs(collection(db, 'invites'));
    const invites = invitesSnap.docs.map(d => d.data());

    const enrichPhotoWithLikesSync = (photo) => {
      const likeProfiles = (photo.likes || []).map(phone => {
        const match = invites.find(inv => inv.phoneNumber === phone);
        return match
          ? {
              phoneNumber: phone,
              name: match.name,
              photoUrl: match.photoUrl && match.photoUrl.trim() !== '' ? match.photoUrl : 'https://placehold.co/100'
            }
          : null;
      }).filter(Boolean);
      return { ...photo, likeProfiles };
    };

    const enriched = fetched.map(enrichPhotoWithLikesSync);
    setPhotos(
      enriched.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds)
    );
  };
  // Like handler for modal - toggle like/unlike using phoneNumber
  const handleLike = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    const phoneNumber = user?.phoneNumber;
    if (!phoneNumber || !selectedPhoto) return;

    const photoRef = doc(db, 'guestGallery', selectedPhoto.id);
    const snap = await getDoc(photoRef);
    const data = snap.data();
    const currentLikes = new Set(data.likes || []);

    if (currentLikes.has(phoneNumber)) {
      currentLikes.delete(phoneNumber); // Unlike
    } else {
      currentLikes.add(phoneNumber); // Like
    }

    await updateDoc(photoRef, { likes: Array.from(currentLikes) });
    fetchPhotos();
  };

  useEffect(() => {
    fetchPhotos();
    
    // Cleanup on unmount
    return () => {
      if (unsubscribeCommentsRef.current) {
        unsubscribeCommentsRef.current();
        unsubscribeCommentsRef.current = null;
      }
    };
  }, []);

  const handleUpload = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const auth = getAuth();
    const user = auth.currentUser;
    const uid = user?.uid || 'anonymous';
    const phoneNumber = user?.phoneNumber || 'unknown';

    const storage = getStorage();

    let uploaderName = 'Anonymous';
    if (phoneNumber !== 'unknown') {
      const q = query(
        collection(db, 'invites'),
        where('phoneNumber', '==', phoneNumber)
      );
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        uploaderName = data.name || uploaderName;
      }
    }

    for (let file of files) {
      const timestampNow = Date.now();
      const storagePath = `guest_uploads/${uid}_${timestampNow}_${file.name}`;
      const storageRef = ref(
        storage,
        storagePath
      );
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'guestGallery'), {
        url,
        uploader: uid,
        uploaderName,
        phoneNumber,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        timestamp: serverTimestamp(),
        storagePath,
      });
    }

    setUploading(false);
    fetchPhotos();
  };

  // Fetch comments for a given photoId and subscribe to updates
  const fetchComments = (photoId) => {
    // Clean up any existing listener
    if (unsubscribeCommentsRef.current) {
      unsubscribeCommentsRef.current();
      unsubscribeCommentsRef.current = null;
    }

    const commentsRef = collection(db, 'guestGallery', photoId, 'comments');
    try {
      unsubscribeCommentsRef.current = onSnapshot(
        commentsRef,
        (snapshot) => {
          const fetchedComments = snapshot.docs.map(doc => doc.data());
          setComments(fetchedComments.sort((a, b) => a.timestamp?.seconds - b.timestamp?.seconds));
        },
        (error) => {
          console.error('Error listening to comments:', error);
          setComments([]);
        }
      );
    } catch (error) {
      console.error('Error setting up comments listener:', error);
    }
  };

  const handleOpenModal = (photo, index) => {
    enrichPhotoWithLikes(photo).then(enriched => setSelectedPhoto(enriched));
    setCurrentIndex(index);
    setShowModal(true);
    setComments([]);
    fetchComments(photo.id);
  };

  const handleCloseModal = () => {
    // Clean up the comments listener when closing the modal
    if (unsubscribeCommentsRef.current) {
      unsubscribeCommentsRef.current();
      unsubscribeCommentsRef.current = null;
    }
    setSelectedPhoto(null);
    setShowModal(false);
    setComments([]);
  };

  const handlePrev = async () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const newPhoto = photos[newIndex];
      setCurrentIndex(newIndex);
      // Refetch the photo document to update likes/comments
      const photoRef = doc(db, 'guestGallery', newPhoto.id);
      const snap = await getDoc(photoRef);
      let photoData;
      if (snap.exists()) {
        photoData = { id: snap.id, ...snap.data() };
      } else {
        photoData = newPhoto;
      }
      enrichPhotoWithLikes(photoData).then(enriched => setSelectedPhoto(enriched));
      fetchComments(newPhoto.id);
    }
  };

  const handleNext = async () => {
    if (currentIndex < photos.length - 1) {
      const newIndex = currentIndex + 1;
      const newPhoto = photos[newIndex];
      setCurrentIndex(newIndex);
      // Refetch the photo document to update likes/comments
      const photoRef = doc(db, 'guestGallery', newPhoto.id);
      const snap = await getDoc(photoRef);
      let photoData;
      if (snap.exists()) {
        photoData = { id: snap.id, ...snap.data() };
      } else {
        photoData = newPhoto;
      }
      enrichPhotoWithLikes(photoData).then(enriched => setSelectedPhoto(enriched));
      fetchComments(newPhoto.id);
    }
  };


  return (
    <div className="page-container" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <h2 className="page-title">
        Photo Gallery
      </h2>
      <p className="page-subtitle" style={{
        marginBottom: '40px',
        maxWidth: '700px'
      }}>
       Snap a pic, or two, or three,
Capture moments wild and free.
From happy tears to dancing feet,
Every smile makes memories sweet.
Upload here anytime you choose,
All your favorites—we’d love to see!
Just one small ask before you share online:
Please wait till we post first—then it’s totally fine.
Thanks so much for all you do,
These treasured memories start with you! 💕📸</p>

      <input
        type="file"
        accept="image/*"
        multiple
        onChange={handleUpload}
        disabled={uploading}
        className="input-field"
        style={{
          marginBottom: '30px',
          width: '100%',
          maxWidth: '400px',
          cursor: 'pointer'
        }}
      />

      {uploading && (
        <div style={{ 
          textAlign: 'center', 
          margin: '20px 0', 
          fontWeight: 'bold',
          color: 'var(--text-primary)',
          fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)',
          fontFamily: 'var(--font-body)'
        }}>
          Uploading photos... ⏳
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '10px',
          marginTop: '30px',
          width: '100%'
        }}
      >
        {photos.map((photo, index) => (
          <div
            key={photo.id}
            style={{
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <img
              src={photo.url}
              alt="Guest Upload"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '6px',
                display: 'block',
                cursor: 'pointer'
              }}
              onClick={() => handleOpenModal(photo, index)}
            />
          </div>
        ))}
      </div>

      {showModal && selectedPhoto && (
        <div
          onClick={handleCloseModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            paddingTop: '80px',
            paddingBottom: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              maxWidth: '85%',
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              marginTop: '20px'
            }}
          >
            <button
              onClick={handleCloseModal}
              style={{
                position: 'sticky',
                top: '10px',
                right: '10px',
                background: 'rgba(0, 0, 0, 0.8)',
                border: '2px solid rgba(255, 255, 255, 0.5)',
                borderRadius: '50%',
                width: '45px',
                height: '45px',
                color: '#fff',
                fontSize: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2001,
                transition: 'all 0.2s ease',
                fontFamily: 'Arial, sans-serif',
                lineHeight: '1',
                alignSelf: 'flex-end',
                marginBottom: '10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.8)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                e.target.style.transform = 'scale(1)';
              }}
              aria-label="Close modal"
            >
              ×
            </button>
            <div style={{ position: 'relative', width: '100%', maxHeight: '60vh', marginBottom: '20px' }}>
              <img
                src={selectedPhoto.url}
                alt="Full Size"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
              <div
                onClick={handlePrev}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '50%',
                  height: '100%',
                  cursor: currentIndex > 0 ? 'pointer' : 'default'
                }}
              />
              <div
                onClick={handleNext}
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '50%',
                  height: '100%',
                  cursor: currentIndex < photos.length - 1 ? 'pointer' : 'default'
                }}
              />
            </div>
            <div style={{ width: '100%', maxWidth: '800px' }}>
              <button 
                onClick={handleLike} 
                className="button-primary"
                style={{ 
                  marginBottom: '10px',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                }}
              >👍 Like</button>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {photos[currentIndex]?.likeProfiles?.map((u, i) => (
                  <img
                    key={i}
                    src={u.photoUrl ? u.photoUrl : 'https://placehold.co/100'}
                    alt={u.name}
                    title={u.name}
                    style={{ width: '30px', height: '30px', borderRadius: '50%' }}
                  />
                ))}
              </div>

              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(1.2rem, 3vw, 1.4rem)',
                color: 'var(--text-primary)',
                marginBottom: '10px'
              }}>Comments</h3>
              <div style={{ marginBottom: '16px' }}>
                {comments.map((c, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '14px',
                    fontFamily: 'system-ui, sans-serif',
                  }}>
                    <img
                      src={c.photoUrl || 'https://placehold.co/100'}
                      alt={c.name}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        marginRight: '12px',
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                    />
                    <div style={{
                      background: 'var(--card-bg-light)',
                      borderRadius: '18px',
                      padding: '10px 14px',
                      maxWidth: '600px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '4px', color: 'var(--text-primary)' }}>{c.name}</span>
                      <span style={{ fontSize: '14px', lineHeight: '1.4', color: 'var(--text-secondary)' }}>{c.text}</span>
                    </div>
                  </div>
                ))}
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!newComment.trim()) return;
                  const auth = getAuth();
                  const user = auth.currentUser;
                  const invitesSnap = await getDocs(collection(db, 'invites'));
                  const invites = invitesSnap.docs.map(d => d.data());
                  const matched = invites.find(i => i.phoneNumber === user.phoneNumber);
                  const name = matched?.name || 'Anonymous';
                  const photoUrl = matched?.photoUrl || 'https://placehold.co/100';

                  await addDoc(collection(db, 'guestGallery', selectedPhoto.id, 'comments'), {
                    name,
                    text: newComment.trim(),
                    timestamp: serverTimestamp(),
                    photoUrl
                  });
                  setNewComment('');
                }}
                style={{
                  display: 'flex',
                  gap: '10px',
                  width: '100%',
                  alignItems: 'center',
                  marginTop: '10px'
                }}
              >
                <input
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="input-field"
                  style={{
                    flex: 1,
                    borderRadius: '20px',
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                  }}
                />
                <button
                  type="submit"
                  className="button-primary"
                  style={{
                    borderRadius: '20px',
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                  }}
                >
                  Post
                </button>
              </form>
              {getAuth().currentUser?.uid === selectedPhoto.uploader && (
                <button
                  onClick={async () => {
                    // Delete from Firestore
                    await deleteDoc(doc(db, 'guestGallery', selectedPhoto.id));
                    // (Removed file deletion from Firebase Storage)
                    // Close modal and refresh
                    handleCloseModal();
                    fetchPhotos();
                  }}
                  className="button-primary"
                  style={{
                    marginTop: '20px',
                    backgroundColor: 'var(--button-danger)',
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)'
                  }}
                >
                  Delete Photo
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default PhotoUpload;